import { useMemo, useState } from "react";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Stepper } from "@/components/marketing/Stepper";
import { PhonePreview } from "@/components/marketing/PhonePreview";
import { ImageUploadField } from "@/components/marketing/ImageUploadField";
import { OfferExpirationField } from "@/components/marketing/OfferExpirationField";
import { RedemptionTimerField } from "@/components/marketing/RedemptionTimerField";
import { promotionSchema } from "@/utils/sms/promotionSchema";
import type { Promotion, PromotionChannelKind, PromotionInput } from "@/types/sms";

const STEP_LABELS = ["Promotion details", "Offer redemption", "Estimated value"];
const FIELD_STEP: Record<string, number> = {
  title: 0,
  description: 0,
  terms: 0,
  image_url: 0,
  channels: 1,
  redemption_instructions: 1,
  estimated_value: 2,
  default_expiration_days: 2,
  default_timer_minutes: 2,
};
const CHANNEL_LABEL: Record<PromotionChannelKind, string> = {
  phone: "Phone (Call in order)",
  in_store: "In store (Dine-in)",
  online: "Online (Order online)",
};

interface PromotionWizardProps {
  open: boolean;
  storeId: string;
  promotion?: Promotion | null;
  onClose: () => void;
  onSubmit: (input: PromotionInput) => Promise<void>;
}

function toFormState(promotion?: Promotion | null): PromotionInput {
  const byChannel = new Map(promotion?.channels.map((c) => [c.channel, c]));
  const channel = (kind: PromotionChannelKind) => {
    const c = byChannel.get(kind);
    return { enabled: c?.enabled ?? false, promo_code: c?.promo_code ?? null, target_url: c?.target_url ?? null };
  };
  return {
    title: promotion?.title ?? "",
    description: promotion?.description ?? null,
    terms: promotion?.terms ?? "Not valid w/ other offers. Valid only at this location.",
    redemption_instructions: promotion?.redemption_instructions ?? "Use the code below at checkout:",
    estimated_value: promotion?.estimated_value ?? 0,
    image_url: promotion?.image_url ?? null,
    default_expiration_days: promotion?.default_expiration_days ?? 7,
    default_timer_minutes: promotion?.default_timer_minutes ?? 60,
    channels: {
      phone: channel("phone"),
      in_store: channel("in_store"),
      online: channel("online"),
    },
  };
}

/** 3-step Promotion wizard: details -> redemption channels -> value/expiration. */
export function PromotionWizard({ open, storeId, promotion, onClose, onSubmit }: PromotionWizardProps) {
  const [step, setStep] = useState(0);
  const [form, setForm] = useState<PromotionInput>(() => toFormState(promotion));
  const [dirty, setDirty] = useState(false);
  const [confirmDiscard, setConfirmDiscard] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const patch = (updates: Partial<PromotionInput>) => {
    setForm((f) => ({ ...f, ...updates }));
    setDirty(true);
  };
  const patchChannel = (kind: PromotionChannelKind, updates: Partial<PromotionInput["channels"][PromotionChannelKind]>) => {
    setForm((f) => ({ ...f, channels: { ...f.channels, [kind]: { ...f.channels[kind], ...updates } } }));
    setDirty(true);
  };

  const enabledChannels = useMemo(
    () => (Object.keys(form.channels) as PromotionChannelKind[]).filter((k) => form.channels[k].enabled),
    [form.channels]
  );

  const requestClose = () => {
    if (dirty) setConfirmDiscard(true);
    else onClose();
  };

  const handleNext = () => {
    if (step === 0 && form.title.trim().length === 0) {
      setErrors({ title: "Title is required" });
      return;
    }
    setErrors({});
    setStep((s) => Math.min(2, s + 1));
  };

  const handleSubmit = async () => {
    const result = promotionSchema.safeParse(form);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      for (const issue of result.error.issues) fieldErrors[String(issue.path[0])] = issue.message;
      setErrors(fieldErrors);
      const earliestStep = Math.min(
        ...Object.keys(fieldErrors).map((field) => FIELD_STEP[field] ?? 2)
      );
      setStep(earliestStep);
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(form);
      setDirty(false);
      onClose();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={(next) => !next && requestClose()}>
        <DialogContent className="max-w-3xl">
          <Stepper steps={STEP_LABELS} current={step} />
          <div className="mt-4 grid gap-6 sm:grid-cols-2">
            <div className="space-y-4">
              {step === 0 && (
                <>
                  <div>
                    <Label htmlFor="promo-title">Title</Label>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      The offer headline customers see on their claim page and in campaign texts.
                    </p>
                    <Input
                      id="promo-title"
                      className="h-11"
                      maxLength={60}
                      value={form.title}
                      onChange={(e) => patch({ title: e.target.value })}
                    />
                    <p className="mt-1 text-xs text-muted-foreground">{form.title.length}/60</p>
                    {errors.title && <p className="text-xs text-status-danger">{errors.title}</p>}
                  </div>
                  <div>
                    <Label htmlFor="promo-description">Description</Label>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      Shown under the headline on the claim page. Customers see it; it is not part of the text itself.
                    </p>
                    <Textarea
                      id="promo-description"
                      className="text-base"
                      maxLength={200}
                      value={form.description ?? ""}
                      onChange={(e) => patch({ description: e.target.value })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="promo-terms">Terms &amp; conditions</Label>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      The fine print on the claim page. Customers see it.
                    </p>
                    <Textarea
                      id="promo-terms"
                      className="text-base"
                      value={form.terms}
                      onChange={(e) => patch({ terms: e.target.value })}
                    />
                    {errors.terms && <p className="text-xs text-status-danger">{errors.terms}</p>}
                  </div>
                  <div>
                    <Label>Image</Label>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      Optional photo on the claim page. Attaching it to a campaign text turns that text into a
                      pricier MMS, so the composer will warn you.
                    </p>
                    <div className="mt-1">
                      <ImageUploadField
                        bucket="sms-promo-images"
                        storeId={storeId}
                        value={form.image_url}
                        onUploaded={(url) => patch({ image_url: url })}
                      />
                    </div>
                  </div>
                </>
              )}

              {step === 1 && (
                <>
                  <p className="text-xs leading-relaxed text-muted-foreground">
                    How customers can redeem: each method you turn on appears as a button on their claim page, with
                    its promo code.
                  </p>
                  {(Object.keys(form.channels) as PromotionChannelKind[]).map((kind) => (
                    <div key={kind} className="rounded-lg border border-border p-3">
                      <div className="flex items-center justify-between">
                        <Label htmlFor={`channel-${kind}`}>{CHANNEL_LABEL[kind]}</Label>
                        <Switch
                          id={`channel-${kind}`}
                          checked={form.channels[kind].enabled}
                          onCheckedChange={(enabled) => patchChannel(kind, { enabled })}
                        />
                      </div>
                      {form.channels[kind].enabled && (
                        <div className="mt-2 space-y-2">
                          <Input
                            className="h-11"
                            placeholder="Add promo code"
                            value={form.channels[kind].promo_code ?? ""}
                            onChange={(e) => patchChannel(kind, { promo_code: e.target.value })}
                          />
                          {kind === "online" && (
                            <Input
                              className="h-11"
                              placeholder="Ordering link"
                              value={form.channels[kind].target_url ?? ""}
                              onChange={(e) => patchChannel(kind, { target_url: e.target.value })}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                  {errors.channels && <p className="text-xs text-status-danger">{errors.channels}</p>}
                  <div>
                    <Label htmlFor="promo-instructions">Redemption instructions</Label>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      Shown to the customer right above their promo code when they claim.
                    </p>
                    <Textarea
                      id="promo-instructions"
                      className="text-base"
                      value={form.redemption_instructions}
                      onChange={(e) => patch({ redemption_instructions: e.target.value })}
                    />
                    {errors.redemption_instructions && (
                      <p className="text-xs text-status-danger">{errors.redemption_instructions}</p>
                    )}
                  </div>
                </>
              )}

              {step === 2 && (
                <>
                  <div>
                    <Label htmlFor="promo-value">Estimated value ($)</Label>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      Internal only: used to measure profit per campaign. Customers never see it.
                    </p>
                    <Input
                      id="promo-value"
                      className="h-11"
                      inputMode="decimal"
                      type="number"
                      min={0}
                      step="0.01"
                      value={form.estimated_value}
                      onChange={(e) => patch({ estimated_value: Number(e.target.value) || 0 })}
                    />
                    {errors.estimated_value && <p className="text-xs text-status-danger">{errors.estimated_value}</p>}
                  </div>
                  <div>
                    <Label>Default offer expiration</Label>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      How long a claim link works after it is sent. Customers see the deadline on the claim page.
                    </p>
                    <OfferExpirationField
                      value={form.default_expiration_days}
                      onChange={(days) => patch({ default_expiration_days: days })}
                    />
                  </div>
                  <div>
                    <Label>Default time to redeem</Label>
                    <p className="text-xs leading-relaxed text-muted-foreground">
                      The countdown that starts when a customer taps a redemption method, shown on their screen.
                    </p>
                    <RedemptionTimerField
                      value={form.default_timer_minutes}
                      onChange={(minutes) => patch({ default_timer_minutes: minutes })}
                    />
                  </div>
                </>
              )}
            </div>

            <div>
              <PhonePreview
                mode="offer"
                title={form.title}
                description={form.description}
                terms={form.terms}
                enabledChannels={enabledChannels}
              />
            </div>
          </div>

          <div className="mt-6 flex items-center justify-between">
            <Button type="button" variant="ghost" onClick={requestClose}>
              Exit
            </Button>
            <div className="flex gap-2">
              {step > 0 && (
                <Button type="button" variant="outline" className="h-11" onClick={() => setStep((s) => s - 1)}>
                  Back
                </Button>
              )}
              {step < 2 ? (
                <Button type="button" className="h-11" onClick={handleNext}>
                  Next
                </Button>
              ) : (
                <Button type="button" className="h-11" onClick={handleSubmit} disabled={submitting}>
                  {promotion ? "Save promotion" : "Create promotion"}
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Discard this promotion?</AlertDialogTitle>
            <AlertDialogDescription>Your unsaved changes will be lost.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Keep editing</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmDiscard(false);
                setDirty(false);
                onClose();
              }}
            >
              Discard
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
